import {
  rebaseImportedBodyOccurrenceBindingId,
  resolveImportedBodyOccurrenceNodeId,
} from '../../imported-model/utils'

//------ 离线包节点引用通用重映射 开始------
/**
 * 离线包中表达装配节点身份的字段名集合。
 *
 * 设计说明：
 * 1. `.puppy` 在导出或导入归一化阶段可能会把显示承载节点折叠到权威装配节点；
 * 2. Mate、基准点、基准轴、基准面的可复算定义都会保存这些字段；
 * 3. 因此节点 alias 必须通过统一递归入口重写，避免不同功能各自实现导致身份漂移。
 */
const PUPPY_ASSEMBLY_NODE_REFERENCE_KEYS = new Set([
  'assemblyId',
  'assemblyNodeId',
  'componentInstanceId',
])

/**
 * 离线包中以前缀保存装配节点 ID 的稳定引用字段。
 */
const PUPPY_ASSEMBLY_STABLE_REFERENCE_KEYS = new Set([
  'stableRef',
])

/**
 * 通用装配节点引用重映射输入。
 */
export interface PuppyAssemblyNodeReferenceRemapInput<TSerializable> {
  /** 原始可序列化数据。 */
  readonly value: TSerializable
  /** 旧节点 ID 到新节点 ID 的确定性映射。 */
  readonly aliasByNodeId: Readonly<Record<string, string>>
}

/**
 * 判断可序列化对象是否是装配拓扑选择引用。
 *
 * @param value 待检查对象。
 * @returns 是否具备 `AssemblyTopologySelectionRef` 的稳定字段。
 */
function isAssemblyTopologySelectionRefRecord(value: Readonly<Record<string, unknown>>): boolean {
  return value.schemaVersion === 1
    && typeof value.assemblyNodeId === 'string'
    && typeof value.bodyBindingId === 'string'
    && typeof value.bodyObjectId === 'string'
    && typeof value.entityKind === 'string'
    && typeof value.topologyIndex === 'number'
    && typeof value.persistentRef === 'string'
}

/**
 * 用已重映射的装配节点身份重定位拓扑引用的 body occurrence。
 *
 * @param value 已完成节点字段 remap 的可序列化对象。
 * @returns 修正 bodyBindingId 后的对象；非拓扑引用保持原对象。
 */
function remapTopologySelectionOccurrenceScope(
  value: Record<string, unknown>,
): Record<string, unknown> {
  if (!isAssemblyTopologySelectionRefRecord(value)) {
    return value
  }

  const bodyBindingId = typeof value.bodyBindingId === 'string' ? value.bodyBindingId : ''
  const assemblyNodeId = typeof value.assemblyNodeId === 'string' ? value.assemblyNodeId : ''
  if (!resolveImportedBodyOccurrenceNodeId(bodyBindingId)) {
    return value
  }

  /**
   * bodyBindingId 中已经携带 occurrence 作用域时，节点折叠后必须同步改写到新的装配节点；
   * 否则导入后基准/Mate 会解析到旧包内部节点，无法命中当前项目模型中的拓扑宿主。
   */
  return {
    ...value,
    bodyBindingId: rebaseImportedBodyOccurrenceBindingId(bodyBindingId, assemblyNodeId),
  }
}

/**
 * 深拷贝可序列化数据。
 *
 * @param value 源数据。
 * @returns 深拷贝后的数据。
 */
function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

/**
 * 解析节点别名链的最终节点 ID。
 *
 * @param nodeId 原节点 ID。
 * @param aliasByNodeId 节点别名映射。
 * @returns 最终节点 ID。
 * @throws 当节点别名链存在循环引用时。
 */
export function resolvePuppyAssemblyNodeReferenceAlias(
  nodeId: string,
  aliasByNodeId: Readonly<Record<string, string>>,
): string {
  let current = nodeId
  const visited = new Set<string>()
  while (aliasByNodeId[current]) {
    if (visited.has(current)) {
      throw new Error(`PuppyCAD 离线包节点引用别名存在循环: ${nodeId}`)
    }
    visited.add(current)
    current = aliasByNodeId[current]!
  }
  return current
}

/**
 * 重映射字符串中位于前缀位置的装配节点引用。
 *
 * 设计说明：
 * `.puppy` 子装配切换时经常只有包根节点存在别名，而 Mate、基准对象等稳定引用
 * 可能保存为 `包根/子节点` 或 `包根:拓扑`。因此 remap 需要沿明确分隔符继承目标根，
 * 避免引用继续指向旧离线包根节点。
 *
 * @param value 待重映射的引用字符串。
 * @param aliasByNodeId 节点别名映射。
 * @param delimiters 允许继承别名的节点边界分隔符。
 * @returns 替换后的引用字符串。
 */
function remapAssemblyNodeReferencePrefix(
  value: string,
  aliasByNodeId: Readonly<Record<string, string>>,
  delimiters: readonly string[],
): string {
  const sourceNodeIds = Object.keys(aliasByNodeId).sort((left, right) => right.length - left.length)
  for (const sourceNodeId of sourceNodeIds) {
    const targetNodeId = resolvePuppyAssemblyNodeReferenceAlias(sourceNodeId, aliasByNodeId)
    if (value === sourceNodeId) {
      return targetNodeId
    }
    if (delimiters.some((delimiter) => value.startsWith(`${sourceNodeId}${delimiter}`))) {
      return `${targetNodeId}${value.slice(sourceNodeId.length)}`
    }
  }
  return value
}

/**
 * 重映射装配节点引用字符串。
 *
 * @param value 装配节点引用。
 * @param aliasByNodeId 节点别名映射。
 * @returns 替换后的装配节点引用。
 */
function remapAssemblyNodeReferenceString(
  value: string,
  aliasByNodeId: Readonly<Record<string, string>>,
): string {
  return remapAssemblyNodeReferencePrefix(value, aliasByNodeId, ['/'])
}

/**
 * 重映射稳定引用字符串的装配节点前缀。
 *
 * @param value 稳定引用字符串。
 * @param aliasByNodeId 节点别名映射。
 * @returns 替换后的稳定引用字符串。
 */
function remapStableReferenceString(
  value: string,
  aliasByNodeId: Readonly<Record<string, string>>,
): string {
  return remapAssemblyNodeReferencePrefix(value, aliasByNodeId, ['/', ':'])
}

/**
 * 判断两个可序列化值在重映射后是否等价。
 *
 * @param left 左值。
 * @param right 右值。
 * @returns 是否可视为同一个确定状态。
 */
function isSerializableEqual(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

/**
 * 写入已重映射字段，重复 key 只允许等价合并。
 *
 * @param target 目标对象。
 * @param key 原始 key，用于错误上下文。
 * @param remappedKey 重映射后的 key。
 * @param value 重映射后的值。
 */
function writeRemappedEntry(
  target: Record<string, unknown>,
  key: string,
  remappedKey: string,
  value: unknown,
): void {
  if (!Object.prototype.hasOwnProperty.call(target, remappedKey)) {
    target[remappedKey] = value
    return
  }

  if (!isSerializableEqual(target[remappedKey], value)) {
    throw new Error(`PuppyCAD 离线包节点引用重映射冲突: ${key} -> ${remappedKey}`)
  }
}

/**
 * 递归重映射可序列化对象中的装配节点引用。
 *
 * @param value 源值。
 * @param aliasByNodeId 节点别名映射。
 * @returns 替换后的值。
 */
function remapNodeReferencesInSerializable<T>(
  value: T,
  aliasByNodeId: Readonly<Record<string, string>>,
): T {
  if (Array.isArray(value)) {
    return value.map((item) => remapNodeReferencesInSerializable(item, aliasByNodeId)) as T
  }
  if (!value || typeof value !== 'object') {
    return value
  }

  const remappedRecord: Record<string, unknown> = {}
  Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
    const remappedKey = remapAssemblyNodeReferenceString(key, aliasByNodeId)
    let remappedValue: unknown
    if (typeof item === 'string' && PUPPY_ASSEMBLY_NODE_REFERENCE_KEYS.has(key)) {
      remappedValue = remapAssemblyNodeReferenceString(item, aliasByNodeId)
    } else if (typeof item === 'string' && PUPPY_ASSEMBLY_STABLE_REFERENCE_KEYS.has(key)) {
      remappedValue = remapStableReferenceString(item, aliasByNodeId)
    } else {
      remappedValue = remapNodeReferencesInSerializable(item, aliasByNodeId)
    }
    writeRemappedEntry(remappedRecord, key, remappedKey, remappedValue)
  })
  return remapTopologySelectionOccurrenceScope(remappedRecord) as T
}

/**
 * 重映射离线包可序列化数据中的装配节点引用。
 *
 * @param input 重映射输入。
 * @returns 使用目标节点 ID 的可序列化数据。
 */
export function remapPuppyAssemblyNodeReferences<TSerializable>(
  input: PuppyAssemblyNodeReferenceRemapInput<TSerializable>,
): TSerializable {
  const clonedValue = cloneSerializable(input.value)
  return remapNodeReferencesInSerializable(clonedValue, input.aliasByNodeId)
}
//------ 离线包节点引用通用重映射 结束------
